'use client';

import { useEffect, useRef, useCallback } from 'react';

export function CustomCursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);
  const isHoveringRef = useRef(false);

  const updateCursorPosition = useCallback(() => {
    const { x, y } = positionRef.current;
    const isHovering = isHoveringRef.current;

    if (dotRef.current) {
      dotRef.current.style.transform = `translate(${x - 6}px, ${y - 6}px)`;
    }

    if (ringRef.current) {
      const offset = isHovering ? 30 : 20;
      ringRef.current.style.transform = `translate(${x - offset}px, ${y - offset}px)`;
      ringRef.current.style.width = isHovering ? '60px' : '40px';
      ringRef.current.style.height = isHovering ? '60px' : '40px';
    }

    rafRef.current = null;
  }, []);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(updateCursorPosition);
    }
  }, [updateCursorPosition]);

  useEffect(() => {
    // Only show custom cursor on desktop
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      // Remove cursor hiding on touch devices
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      positionRef.current = { x: e.clientX, y: e.clientY };

      // Show cursors on first move
      if (dotRef.current) dotRef.current.style.opacity = '1';
      if (ringRef.current) ringRef.current.style.opacity = '1';

      // Check for interactive elements (cache the check)
      const target = e.target as HTMLElement;
      const isInteractive =
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a') ||
        target.closest('button') ||
        target.dataset['cursorHover'] === 'true';

      isHoveringRef.current = !!isInteractive;

      scheduleUpdate();
    };

    const handleMouseLeave = () => {
      if (dotRef.current) dotRef.current.style.opacity = '0';
      if (ringRef.current) ringRef.current.style.opacity = '0';
    };

    const handleMouseEnter = () => {
      if (dotRef.current) dotRef.current.style.opacity = '1';
      if (ringRef.current) ringRef.current.style.opacity = '1';
    };

    const handleMouseDown = () => {
      if (dotRef.current) {
        dotRef.current.firstElementChild?.classList.add('scale-80');
      }
      if (ringRef.current) {
        ringRef.current.firstElementChild?.classList.add('scale-90');
      }
    };

    const handleMouseUp = () => {
      if (dotRef.current) {
        dotRef.current.firstElementChild?.classList.remove('scale-80');
      }
      if (ringRef.current) {
        ringRef.current.firstElementChild?.classList.remove('scale-90');
      }
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [scheduleUpdate]);

  // Don't render on SSR
  if (typeof window === 'undefined') return null;

  return (
    <>
      {/* Hide default cursor globally */}
      <style jsx global>{`
        @media (pointer: fine) {
          * {
            cursor: none !important;
          }
        }
      `}</style>

      {/* Cursor dot */}
      <div
        ref={dotRef}
        className="fixed top-0 left-0 pointer-events-none z-[9999] mix-blend-difference"
        style={{ opacity: 0, willChange: 'transform' }}
      >
        <div
          className="rounded-full bg-white w-3 h-3 transition-transform duration-150 ease-out"
        />
      </div>

      {/* Cursor ring */}
      <div
        ref={ringRef}
        className="fixed top-0 left-0 pointer-events-none z-[9998] mix-blend-difference"
        style={{ opacity: 0, willChange: 'transform', transition: 'width 0.2s ease, height 0.2s ease' }}
      >
        <div
          className="rounded-full border border-white/50 w-10 h-10 transition-transform duration-150 ease-out"
        />
      </div>
    </>
  );
}
